import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Printer, ArrowLeft, Download, Loader2, Eye } from "lucide-react";
import { CertificateDocument } from "@/components/certificate/CertificateDocument";
import { getTestDetail, apiError } from "@/api";
import { formatDate } from "@/lib/testFormat";

function toCertificateCandidate(detail) {
  const { test, savedResult, session } = detail;
  const c = test.candidate || {};
  return {
    id: test.testId,
    name: c.name || "Unknown",
    age: c.age,
    licenseType: c.licenseType || "Two-Wheeler (MCWG)",
    vehicleType: c.vehicleType || "—",
    testCenter: c.testCenter || "—",
    rfid: test.rfid,
    testDate: session?.startMs || savedResult?.evaluatedAt,
    overallScore: savedResult?.overallScore ?? 0,
    result: test.isReference ? "REFERENCE" : savedResult?.result || "PENDING",
    scores: savedResult?.scores || {},
    sensorData: savedResult?.sensorMetrics || {},
    certificateUrl: savedResult?.certificateUrl || null,
  };
}

export default function CertificatePage() {
  const { id } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const generating = false;
  const pdfError = null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getTestDetail(id)
      .then((d) => !cancelled && setCandidate(toCertificateCandidate(d)))
      .catch((e) => !cancelled && setLoadError(apiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Older evaluations may have a stored PDF; new ones use the browser's Print / Save as PDF
  useEffect(() => {
    setPdfUrl(candidate?.result === "PASS" ? candidate.certificateUrl : null);
  }, [candidate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return <CertificateNotAvailable text={`Could not load ${id}: ${loadError}`} />;
  }

  if (!candidate || candidate.result !== "PASS") {
    return <CertificateNotAvailable />;
  }

  const formattedDate = formatDate(candidate.testDate);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <CertificateControls pdfUrl={pdfUrl} generating={generating} />
        <CertificateDocument candidate={candidate} formattedDate={formattedDate} />

        {/* PDF Download section */}
        {(pdfUrl || generating || pdfError) && (
          <div className="print:hidden mt-6 bg-zinc-900 border border-white/10 rounded-xl p-5">
            <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400 mb-3 block">
              Firebase Generated PDF
            </span>
            {generating && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating PDF from server...
              </div>
            )}
            {pdfUrl && (
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-pdf-btn"
                    className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-2.5 px-5 rounded-lg hover:bg-yellow-400 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="view-pdf-btn"
                    className="inline-flex items-center gap-2 bg-zinc-800 text-white border border-white/10 font-bold py-2.5 px-5 rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View PDF
                  </a>
                </div>
                <iframe
                  src={pdfUrl}
                  title="Certificate PDF"
                  className="w-full h-[500px] rounded-lg border border-white/10"
                />
              </div>
            )}
            {pdfError && (
              <p className="text-xs text-zinc-500">
                PDF generation not available: {pdfError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CertificateControls({ pdfUrl, generating }) {
  return (
    <div className="print:hidden mb-8 flex items-center justify-between">
      <Link
        to="/dashboard"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>
      <button
        data-testid="print-certificate-btn"
        onClick={() => window.print()}
        className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-2.5 px-5 rounded-lg hover:bg-yellow-400 transition-colors"
      >
        <Printer className="w-4 h-4" />
        Print / Download PDF
      </button>
    </div>
  );
}

function CertificateNotAvailable({ text }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <ShieldCheck className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold text-white mb-2">
          Certificate Not Available
        </h2>
        <p className="text-sm text-zinc-400 mb-6">
          {text || "Only candidates whose evaluated test is PASS have a certificate."}
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-2.5 px-5 rounded-lg hover:bg-yellow-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
